import { c as _c } from "react/compiler-runtime";
import * as RadioGroupPrimitives from "@radix-ui/react-radio-group";
import React from "react";
import { cx, focusRing } from "@/lib/tailwind-utils";
import { BOOLEAN_FILTER_OPTIONS } from "./boolean-options";
import type { FilterInputProps } from "./types";
export { BOOLEAN_FILTER_OPTIONS };

/**
 * The one choice a boolean filter holds. A caller still on a multiselect's
 * list shape may hand over an array: one item is that choice, and several
 * mean Any, the same as a stored multi-value filter reads elsewhere.
 */
export const singleBooleanValue = (value: unknown): unknown => {
  if (!Array.isArray(value)) return value;
  return value.length === 1 ? value[0] : undefined;
};

// Radix radio values are strings; "" can't be an item value, so "Any" gets its own.
const ANY_VALUE = "__any__";

/**
 * Segmented "Any | Yes | No" control for a filter that is either on, off, or
 * not applied. A switch can't show the third state and a dropdown hides two
 * options behind a click, so every state stays visible and one click away.
 * "Any" clears the filter. Options default to Yes/No; pass custom ones for
 * labels like Active/Paused or an extra "Not set" state.
 */
const BooleanFilter: React.FC<Pick<FilterInputProps, "value" | "onChange" | "options" | "label" | "focusRef">> = t0 => {
  const $ = _c(20);
  const {
    value,
    onChange,
    options,
    label,
    focusRef
  } = t0;
  const segments = options?.length ? options : BOOLEAN_FILTER_OPTIONS;
  let t1;
  if ($[0] !== value) {
    t1 = singleBooleanValue(value);
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const current = t1;
  const selected = current === undefined || current === null || current === "" ? ANY_VALUE : String(current);
  let t2;
  if ($[2] !== segments || $[3] !== selected) {
    t2 = segments.some(o => String(o.value) === selected) ? selected : ANY_VALUE;
    $[2] = segments;
    $[3] = selected;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const focusValue = t2;
  let t3;
  if ($[5] !== onChange || $[6] !== segments) {
    t3 = next => {
      if (next === ANY_VALUE) {
        onChange(undefined);
        return;
      }
      const option = segments.find(o_0 => String(o_0.value) === next);
      onChange(option ? option.value : next);
    };
    $[5] = onChange;
    $[6] = segments;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      value: ANY_VALUE,
      label: "Any"
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== segments) {
    t5 = [t4, ...segments];
    $[9] = segments;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== focusRef || $[12] !== focusValue || $[13] !== t5) {
    t6 = t5.map(segment => <RadioGroupPrimitives.Item key={String(segment.value)} ref={String(segment.value) === focusValue ? focusRef : undefined} value={String(segment.value)} className={cx("min-w-0 flex-auto rounded-[5px] px-2.5 py-1 text-center text-body leading-tight text-foreground-secondary transition-colors", "hover:bg-hover hover:text-foreground", "data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:hover:bg-primary", focusRing)}>{segment.label}</RadioGroupPrimitives.Item>);
    $[11] = focusRef;
    $[12] = focusValue;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== label || $[16] !== selected || $[17] !== t3 || $[18] !== t6) {
    t7 = <RadioGroupPrimitives.Root aria-label={label} value={selected} onValueChange={t3} orientation="horizontal" loop={true} className="flex w-full items-stretch rounded-md border border-border bg-background p-0.5 shadow-sm">{t6}</RadioGroupPrimitives.Root>;
    $[15] = label;
    $[16] = selected;
    $[17] = t3;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
};
export default BooleanFilter;